(function () {
  'use strict';

  const LOWER_STUCK_CLASS = 'is-menu-bar-stuck';
  const WRAPPER_STUCK_CLASS = 'is-lower-menu-stuck';
  const MOBILE_SOLID_CLASS = 'is-mobile-header-solid';
  const BODY_STICKY_CLASS = 'has-header-sticky';
  const DESKTOP_QUERY = '(min-width: 1025px)';
  const MOBILE_QUERY = '(max-width: 1024px)';

  function initMenuBarSticky() {
    const wrapper = document.querySelector('[data-header-sticky-menu-bar]');

    if (!wrapper) {
      return;
    }

    const lowerHeader = wrapper.querySelector('[data-header-sticky-lower]');
    const sectionPadding = wrapper.querySelector('.theme__header .section-padding');
    const desktopMedia = window.matchMedia(DESKTOP_QUERY);
    const mobileMedia = window.matchMedia(MOBILE_QUERY);
    const isHomeOverlay = wrapper.hasAttribute('data-home-menu-overlay');

    document.body.classList.add(BODY_STICKY_CLASS);

    let desktopState = null;
    let mobileState = null;

    function createDesktopSticky() {
      if (!lowerHeader || desktopState) {
        return;
      }

      const sentinel = document.createElement('div');
      sentinel.className = 'header__sticky-sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      lowerHeader.parentNode.insertBefore(sentinel, lowerHeader);

      const placeholder = document.createElement('div');
      placeholder.className = 'header__sticky-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      lowerHeader.insertAdjacentElement('afterend', placeholder);

      let isStuck = false;
      let lockedHeight = 0;
      let observer = null;
      let rafId = 0;

      function getLowerHeight() {
        return Math.ceil(lowerHeader.getBoundingClientRect().height || lowerHeader.offsetHeight || 0);
      }

      function cacheHeight() {
        const wasStuck = isStuck;

        if (wasStuck) {
          lowerHeader.classList.remove(LOWER_STUCK_CLASS);
        }

        const height = getLowerHeight();

        if (wasStuck) {
          lowerHeader.classList.add(LOWER_STUCK_CLASS);
        }

        if (height > 0) {
          lockedHeight = height;
          document.documentElement.style.setProperty('--header-lower-height', `${height}px`);
        }

        return lockedHeight;
      }

      function syncPlaceholder() {
        const height = lockedHeight || cacheHeight();

        if (!desktopMedia.matches) {
          placeholder.style.height = '0px';
          return;
        }

        if (isHomeOverlay || isStuck) {
          placeholder.style.height = `${height}px`;
        } else {
          placeholder.style.height = '0px';
        }
      }

      function stick() {
        if (isStuck || !desktopMedia.matches) {
          return;
        }

        cacheHeight();
        syncPlaceholder();
        lowerHeader.classList.add(LOWER_STUCK_CLASS);
        wrapper.classList.add(WRAPPER_STUCK_CLASS);
        isStuck = true;
      }

      function unstick() {
        if (!isStuck || document.documentElement.hasAttribute('data-scroll-locked')) {
          return;
        }

        lowerHeader.classList.remove(LOWER_STUCK_CLASS);
        wrapper.classList.remove(WRAPPER_STUCK_CLASS);
        isStuck = false;
        cacheHeight();
        syncPlaceholder();
      }

      function syncFromScroll() {
        if (!desktopMedia.matches) {
          if (isStuck) {
            lowerHeader.classList.remove(LOWER_STUCK_CLASS);
            wrapper.classList.remove(WRAPPER_STUCK_CLASS);
            isStuck = false;
          }
          syncPlaceholder();
          return;
        }

        if (sentinel.getBoundingClientRect().top <= 0) {
          stick();
        } else {
          unstick();
        }
      }

      function onScrollOrResize() {
        if (rafId) {
          return;
        }

        rafId = window.requestAnimationFrame(() => {
          rafId = 0;
          syncFromScroll();
        });
      }

      function createObserver() {
        if (observer) {
          observer.disconnect();
          observer = null;
        }

        cacheHeight();
        syncPlaceholder();
        syncFromScroll();

        if (!desktopMedia.matches) {
          return;
        }

        observer = new IntersectionObserver(() => syncFromScroll(), {
          root: null,
          threshold: 0,
          rootMargin: '0px',
        });

        observer.observe(sentinel);
      }

      function destroy() {
        if (observer) {
          observer.disconnect();
          observer = null;
        }

        window.removeEventListener('scroll', onScrollOrResize);
        unstick();
        placeholder.style.height = '0px';
      }

      cacheHeight();
      syncPlaceholder();
      createObserver();
      window.addEventListener('scroll', onScrollOrResize, { passive: true });

      desktopState = {
        destroy,
        resize() {
          cacheHeight();
          syncPlaceholder();
          createObserver();
        },
      };
    }

    function createMobileHomeOverlay() {
      if (!isHomeOverlay || !sectionPadding || mobileState) {
        return;
      }

      const sentinel = document.createElement('div');
      sentinel.className = 'header__mobile-overlay-sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      sectionPadding.parentNode.insertBefore(sentinel, sectionPadding);

      const placeholder = document.createElement('div');
      placeholder.className = 'header__mobile-overlay-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      sectionPadding.insertAdjacentElement('afterend', placeholder);

      let isSolid = false;
      let lockedHeight = 0;
      let observer = null;
      let rafId = 0;

      function getBarHeight() {
        return Math.ceil(sectionPadding.getBoundingClientRect().height || sectionPadding.offsetHeight || 0);
      }

      function cacheHeight() {
        const wasSolid = isSolid;

        if (wasSolid) {
          sectionPadding.classList.remove(LOWER_STUCK_CLASS);
          wrapper.classList.remove(MOBILE_SOLID_CLASS);
        }

        const height = getBarHeight();

        if (wasSolid) {
          sectionPadding.classList.add(LOWER_STUCK_CLASS);
          wrapper.classList.add(MOBILE_SOLID_CLASS);
        }

        if (height > 0) {
          lockedHeight = height;
          document.documentElement.style.setProperty('--header-mobile-bar-height', `${height}px`);
        }

        return lockedHeight;
      }

      function syncPlaceholder() {
        const height = lockedHeight || cacheHeight();

        if (!mobileMedia.matches) {
          placeholder.style.height = '0px';
          return;
        }

        // Always reserve space on homepage mobile so absolute/fixed never jumps.
        placeholder.style.height = `${height}px`;
      }

      function makeSolid() {
        if (isSolid || !mobileMedia.matches) {
          return;
        }

        cacheHeight();
        syncPlaceholder();
        sectionPadding.classList.add(LOWER_STUCK_CLASS);
        wrapper.classList.add(MOBILE_SOLID_CLASS);
        isSolid = true;
      }

      function makeTransparent() {
        if (!isSolid || document.documentElement.hasAttribute('data-scroll-locked')) {
          return;
        }

        sectionPadding.classList.remove(LOWER_STUCK_CLASS);
        wrapper.classList.remove(MOBILE_SOLID_CLASS);
        isSolid = false;
        cacheHeight();
        syncPlaceholder();
      }

      function syncFromScroll() {
        if (!mobileMedia.matches) {
          makeTransparent();
          placeholder.style.height = '0px';
          return;
        }

        if (sentinel.getBoundingClientRect().top <= 0) {
          makeSolid();
        } else {
          makeTransparent();
        }
      }

      function onScrollOrResize() {
        if (rafId) {
          return;
        }

        rafId = window.requestAnimationFrame(() => {
          rafId = 0;
          syncFromScroll();
        });
      }

      function createObserver() {
        if (observer) {
          observer.disconnect();
          observer = null;
        }

        cacheHeight();
        syncPlaceholder();
        syncFromScroll();

        if (!mobileMedia.matches) {
          return;
        }

        observer = new IntersectionObserver(() => syncFromScroll(), {
          root: null,
          threshold: 0,
          rootMargin: '0px',
        });

        observer.observe(sentinel);
      }

      function destroy() {
        if (observer) {
          observer.disconnect();
          observer = null;
        }

        window.removeEventListener('scroll', onScrollOrResize);
        makeTransparent();
        placeholder.style.height = '0px';
      }

      cacheHeight();
      syncPlaceholder();
      createObserver();
      window.addEventListener('scroll', onScrollOrResize, { passive: true });

      mobileState = {
        destroy,
        resize() {
          cacheHeight();
          syncPlaceholder();
          createObserver();
        },
      };
    }

    function syncModes() {
      if (desktopMedia.matches) {
        if (mobileState) {
          mobileState.destroy();
          mobileState = null;
        }

        if (lowerHeader) {
          createDesktopSticky();
          if (desktopState) {
            desktopState.resize();
          }
        }
      } else if (mobileMedia.matches && isHomeOverlay) {
        if (desktopState) {
          desktopState.destroy();
          desktopState = null;
        }

        createMobileHomeOverlay();
        if (mobileState) {
          mobileState.resize();
        }
      } else {
        if (desktopState) {
          desktopState.destroy();
          desktopState = null;
        }

        if (mobileState) {
          mobileState.destroy();
          mobileState = null;
        }
      }
    }

    syncModes();

    const onResize = () => syncModes();

    if (typeof desktopMedia.addEventListener === 'function') {
      desktopMedia.addEventListener('change', onResize);
      mobileMedia.addEventListener('change', onResize);
    } else {
      desktopMedia.addListener(onResize);
      mobileMedia.addListener(onResize);
    }

    window.addEventListener('resize', onResize);
    document.addEventListener('shopify:section:load', onResize);
    document.addEventListener('shopify:section:unload', onResize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMenuBarSticky);
  } else {
    initMenuBarSticky();
  }
})();
